import { Component } from '@angular/core';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { RateTemplate } from '../../_types/rate-template';
import { RatesTemplatesService } from '../../_services/rates-templates.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { RateTemplateLineComponent } from './line/line.component';
import { RateTemplateLine } from '../../_types/rate-template-line';
import { RatesTemplatesLinesService } from '../../_services/rates-templates-lines.service';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { LoginService } from 'src/app/modules/login/login.service';

@Component({
    selector: 'app-rate-template',
    templateUrl: './rate-template.component.html',
    styleUrls: ['./rate-template.component.scss'],
    standalone: false
})
export class RateTemplateComponent {
  public rateTemplateFormGroup!: RxFormGroup;
  public rateTemplate: RateTemplate = new RateTemplate();
  public allowWrite: boolean = false;
  public allowDelete: boolean = false;

  public rateTemplateLines: RateTemplateLine[] = [];
  public loadingRateTemplateLines: boolean = false;
  public totalRateTemplateLinesRecords: number = 0;
  public type: string = '';

  constructor(
    private ratesTemplatesService: RatesTemplatesService,
    private config: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private ratesTemplatesLinesService: RatesTemplatesLinesService,
    private dialogService: DialogService,
    private loginService: LoginService
  ) {

    this.rateTemplateFormGroup = this.formBuilder.formGroup<RateTemplate>(this.rateTemplate) as RxFormGroup;
  }

  async ngOnInit() {
    let rateTemplateId = this.config.data.rateTemplateId;
    this.type = this.config.data.type;

    if (rateTemplateId) {
      await this.loadRateTemplate(rateTemplateId);
    } else {
      this.rateTemplate.rateId = this.config.data.rateId;
    }

    this.rateTemplateFormGroup.patchValue(this.rateTemplate);

    this.allowWrite = this.loginService.hasPermission(this.type + "_RATES.TEMPLATES_WRITE");
    this.allowDelete = this.loginService.hasPermission(this.type + "_RATES.TEMPLATES_DELETE");

  }

  async loadRateTemplate(rateTemplateId: string) {

    this.rateTemplate = await firstValueFrom(this.ratesTemplatesService.get(rateTemplateId));
    this.loadRateTemplateLines();
  }

  async loadRateTemplateLines() {
    this.loadingRateTemplateLines = true
    let params: IParamsCollection = {
      include: ['rateLine']
    }
    let filters: any = {
      rateTemplateId: new Filter(FilterOperation.equals, "rateTemplateId", "", this.rateTemplate.id),
    };
    params.filter = FilterBuilder.fromObject(filters).build();

    let response = await firstValueFrom(this.ratesTemplatesLinesService.all(params));
    this.rateTemplateLines = response.data;
    this.totalRateTemplateLinesRecords = response.meta['total'];

    this.loadingRateTemplateLines = false;
  }

  async editRateTemplateLine(rateTemplateLineId: string | null) {
    let ref = this.dialogService.open(RateTemplateLineComponent, {
      closable: true,
      modal: true,
      header: 'Añadir línea de baremo',
      width: '80%',
      data: {
        existentRateLines: this.rateTemplateLines,
        rateTemplateId: this.rateTemplate.id,
        rateId: this.rateTemplate.rateId,
        rateTemplateLineId: rateTemplateLineId
      }
    });

    ref.onClose.subscribe((data: any) => {
      this.loadRateTemplateLines();
    });
  }

  async deleteRateTemplateLine(id: string) {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar la línea de baremo?",
      header: "Eliminar línea de baremo",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.ratesTemplatesLinesService.delete(id).subscribe({
          next: (data: any) => {
            this.messageService.add({ closable: true, severity: 'success', detail: "Línea eliminada correctamente." });
            this.loadRateTemplateLines();
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }


  async save() {
    let valid = this.formValidationService.validateForm(this.rateTemplateFormGroup);
    if (valid) {
      this.rateTemplate = Object.assign(this.rateTemplate, this.rateTemplateFormGroup.value);

      let rateTemplate = await firstValueFrom(this.ratesTemplatesService.save(this.rateTemplate));
      this.messageService.add({ closable: true, severity: 'success', summary: "Éxito", detail: "Plantilla guardada correctamente." });
      if (this.rateTemplate.id) {
        this.dynamicDialogRef.close();
      } else {
        this.rateTemplate.id = rateTemplate.id;
        this.rateTemplateFormGroup.patchValue(this.rateTemplate);
      }

    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  async delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar la plantilla de baremo?",
      header: "Eliminar plantilla",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.ratesTemplatesService.delete(this.rateTemplate.id).subscribe({
          next: (data: any) => {
            this.messageService.add({ closable: true, severity: 'success', detail: "Plantilla eliminada correctamente." });
            this.cancel();
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      },
      reject: (error: any) => {
        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
      }
    });
  }

  cancel() {
    this.dynamicDialogRef.close();
  }
}
